import i18n from "i18next";
import { resolveKpiPivotSubtitle } from "pages/GlobalExecutiveDashboard/KpiView/kpiViewUtils";
import { getIntlLocale } from "util/locale";
/**
 * CX alert view-model helpers.
 * Maps legacy `notice/alerts` API rows to the refactored UI shape.
 * TODO: remove mock fallbacks when API returns last_payload, fire_count_30d, is_active.
 */

/** @typedef {'critical'|'warning'|'info'} AlertSeverity */
/** @typedef {'daily'|'weekly'|'hourly'} AlertFrequency */

export const SEVERITY_CONFIG = {
  critical: {
    icon: "🔴",
    className: "severityCritical",
    labelKey: "alerts_page.severity_critical",
  },
  warning: {
    icon: "🟡",
    className: "severityWarning",
    labelKey: "alerts_page.severity_warning",
  },
  info: {
    icon: "🔵",
    className: "severityInfo",
    labelKey: "alerts_page.severity_info",
  },
};

export const METRIC_LABEL_KEYS = {
  positive_rate: "alerts_page.metric_positive_rate",
  praise_rate: "alerts_page.metric_praise_rate",
  average_rating: "alerts_page.metric_average_rating",
  volume: "alerts_page.metric_volume",
};

export const MONITORED_METRIC_CHIPS = [
  { key: "positive_rate", labelKey: METRIC_LABEL_KEYS.positive_rate },
  { key: "praise_rate", labelKey: METRIC_LABEL_KEYS.praise_rate },
  { key: "average_rating", labelKey: METRIC_LABEL_KEYS.average_rating },
];

const MS_DAY = 24 * 60 * 60 * 1000;

function parseLegacyAlertDate(raw) {
  if (!raw) return null;
  if (raw instanceof Date) return raw;
  const asDate = new Date(raw);
  if (!Number.isNaN(asDate.getTime())) return asDate;
  const parts = String(raw).split(" ");
  if (parts.length >= 5) {
    const rebuilt = `${parts[1]} ${parts[2]} ${parts[3]} ${parts[4]}`;
    const parsed = new Date(rebuilt);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return null;
}

function normalizeFrequency(raw) {
  if (Array.isArray(raw) && raw.length) {
    const f = String(raw[0]).toLowerCase();
    if (f.includes("week")) return "weekly";
    if (f.includes("day")) return "daily";
    return f;
  }
  if (typeof raw === "string") {
    const f = raw.toLowerCase();
    if (f.includes("week")) return "weekly";
    if (f.includes("day")) return "daily";
  }
  return "daily";
}

function normalizeFrequencyList(raw) {
  if (!Array.isArray(raw)) return ["daily"];
  const out = raw
    .map((f) => String(f).toLowerCase())
    .filter((f) => f === "daily" || f === "weekly");
  return out.length ? out : ["daily"];
}

/**
 * TODO: remove mock — infer minimal payload when backend has not stored last_payload yet.
 */
function buildMockLastPayload(alert, firedAt) {
  if (!firedAt) return null;
  const frequency = normalizeFrequency(alert.frequency);
  return {
    alert_id: alert.alert_id,
    kpi_name: alert.alert_title,
    fired_at: firedAt.toISOString(),
    frequency,
    severity: "warning",
    metrics: {
      positive_rate: {
        current: 58.2,
        baseline: 65.1,
        delta_pp: -6.9,
        z_score: -2.4,
        is_anomaly: true,
        direction: "below",
      },
    },
    reason: "single_quality_metric_dropped",
    _mock: true,
  };
}

/**
 * @param {object} raw — API alert row from notice/alerts
 */
export function normalizeAlertRecord(raw) {
  const lastFiredAt = parseLegacyAlertDate(raw?.lastAlertDate);
  const lastPayload =
    raw?.last_payload ??
    (lastFiredAt ? buildMockLastPayload(raw, lastFiredAt) : null);

  const fireCount30d =
    typeof raw?.fire_count_30d === "number"
      ? raw.fire_count_30d
      : lastFiredAt &&
        Date.now() - lastFiredAt.getTime() <= 30 * MS_DAY
      ? 1
      : 0;

  return {
    alert_id: raw?.alert_id,
    kpi_id: raw?.kpi_id ?? null,
    kpi_name: raw?.alert_title ?? "",
    pivot_subtitle: resolveKpiPivotSubtitle(raw),
    pivot_filter: raw?.pivot_filter ?? null,
    pivots: Array.isArray(raw?.pivots) ? raw.pivots : [],
    team_id: raw?.team_id ?? raw?.alert_owner ?? null,
    team_name: raw?.team_name ?? null,
    is_power_user_view: Boolean(raw?.is_power_user_view),
    frequency: normalizeFrequencyList(raw?.frequency),
    primary_frequency: normalizeFrequency(raw?.frequency),
    is_active: raw?.is_active ?? true,
    last_fired_at: lastPayload?.fired_at ?? lastFiredAt?.toISOString() ?? null,
    last_severity: lastPayload?.severity ?? raw?.last_severity ?? null,
    fire_count_30d: fireCount30d,
    last_payload: lastPayload,
    alert_kpi: Array.isArray(raw?.alert_kpi) ? raw.alert_kpi : [],
    send_to: Array.isArray(raw?.send_to) ? raw.send_to : [],
    send_sms: Boolean(raw?.send_sms),
    updateDate: raw?.updateDate ?? null,
    _raw: raw,
  };
}

export function getMonitoredMetricKeys(record) {
  const raw = record?._raw ?? record;
  if (Array.isArray(raw?.monitored_metrics) && raw.monitored_metrics.length) {
    return raw.monitored_metrics.map(String);
  }
  if (Array.isArray(raw?.metrics) && raw.metrics.length) {
    return raw.metrics.map(String);
  }
  const payloadMetrics = record?.last_payload?.metrics;
  if (payloadMetrics && Object.keys(payloadMetrics).length) {
    return Object.keys(payloadMetrics);
  }
  return MONITORED_METRIC_CHIPS.map(({ key }) => key);
}

export function getMonitoredMetricChips(record) {
  const keys = getMonitoredMetricKeys(record);
  const known = new Map(MONITORED_METRIC_CHIPS.map((chip) => [chip.key, chip]));
  return keys.map(
    (key) =>
      known.get(key) ?? {
        key,
        labelKey: METRIC_LABEL_KEYS[key] ?? null,
      }
  );
}

export function formatFireCount30d(count, t) {
  const value = typeof count === "number" ? count : 0;
  if (value <= 0) return t("alerts_page.fire_count_zero");
  return t("alerts_page.fire_count_label", { count: value });
}

export function normalizeAlertList(alerts) {
  return (alerts ?? []).map(normalizeAlertRecord);
}

export function getAnomalyMetrics(payload) {
  if (!payload?.metrics) return [];
  return Object.entries(payload.metrics).filter(
    ([, metric]) => metric?.is_anomaly
  );
}

export function formatMetricDelta(metricKey, metric, t) {
  if (!metric) return "";
  if (metricKey === "positive_rate" || metricKey === "praise_rate") {
    const cur = Number(metric.current).toFixed(1);
    const base = Number(metric.baseline).toFixed(1);
    const delta = Number(metric.delta_pp ?? metric.delta ?? 0).toFixed(1);
    const sign = Number(delta) > 0 ? "+" : "";
    return `%${base} → %${cur} (${sign}${delta} p.p.)`;
  }
  if (metricKey === "average_rating") {
    const cur = Number(metric.current).toFixed(1);
    const base = Number(metric.baseline).toFixed(1);
    const delta = Number(metric.delta ?? cur - base).toFixed(1);
    const sign = Number(delta) > 0 ? "+" : "";
    return `${base} → ${cur} (${sign}${delta})`;
  }
  if (metricKey === "volume") {
    const cur = Number(metric.current).toFixed(0);
    const base = Number(metric.baseline).toFixed(0);
    const pct =
      metric.delta_pct ??
      (base !== "0" ? ((Number(cur) - Number(base)) / Number(base)) * 100 : 0);
    const sign = pct > 0 ? "+" : "";
    return `${Number(base).toLocaleString(getIntlLocale(i18n.language))} → ${Number(cur).toLocaleString(getIntlLocale(i18n.language))} (${sign}${Number(pct).toFixed(1)}%)`;
  }
  return "";
}

export function formatRelativeTime(isoOrDate, t) {
  const date =
    isoOrDate instanceof Date ? isoOrDate : parseLegacyAlertDate(isoOrDate);
  if (!date) return t("alerts_page.never_fired");
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return t("alerts_page.time_just_now");
  if (diffMin < 60) return t("alerts_page.time_minutes_ago", { count: diffMin });
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return t("alerts_page.time_hours_ago", { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return t("alerts_page.time_days_ago", { count: diffDays });
  return formatAbsoluteDate(date);
}

export function formatAbsoluteDate(isoOrDate) {
  const date =
    isoOrDate instanceof Date ? isoOrDate : parseLegacyAlertDate(isoOrDate);
  if (!date) return "—";
  return date.toLocaleString(getIntlLocale(i18n.language), {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatFrequencyBadge(freq, t) {
  const f = String(freq || "daily").toLowerCase();
  if (f === "weekly") return t("alerts_page.freq_weekly");
  return t("alerts_page.freq_daily");
}

export function formatAlertTeamLabel(record, t) {
  if (record?.is_power_user_view) {
    return t("alerts_page.team_power_user_view", "Power User");
  }
  const name = record?.team_name;
  if (name && String(name).trim()) {
    return String(name).trim();
  }
  return t("alerts_page.team_unknown", "—");
}

export function maxZScore(payload) {
  const metrics = payload?.metrics ?? {};
  let max = null;
  Object.values(metrics).forEach((m) => {
    if (m?.z_score == null) return;
    const abs = Math.abs(Number(m.z_score));
    if (max == null || abs > max) max = abs;
  });
  return max;
}

/**
 * TODO: remove mock — replace with AlertHistory API rows.
 */
export function buildAlertHistoryEntries(record) {
  if (!record?.last_payload) return [];
  return [record.last_payload];
}
